import { useEffect, useState } from 'react';
import cn from 'classnames';
import { useDebouncedCallback } from 'use-debounce';

import InterceptionsTable from 'components/Interceptions/InterceptionsTable';

import { Header } from '../../components/Header/Header';
import InterceptionsFilterSidebar from '../../components/Interceptions/InterceptionsFilterSidebar';
import InterceptionsInfoSidebar from '../../components/Interceptions/InterceptionsInfoSidebar';
import InterceptionsMemberInfoSidebar from '../../components/Interceptions/InterceptionsMemberInfoSidebar';
import InterceptionsNetworkInfoSidebar from '../../components/Interceptions/InterceptionsNetworkInfoSidebar';
import { TextInput } from '../../components/ui';
import { useAppDispatch } from '../../hooks';
import {
  Interception,
  InterceptionMember,
  InterceptionNetwork,
} from '../../interfaces/interceptions';
import {
  getInterceptionDepartmentsThunk,
  getInterceptionMembersThunk,
  getInterceptionNetworksThunk,
  getInterceptionsListThunk,
} from '../../store/slices/interception/actions';

const InterceptionsScreen = () => {
  const [openedInterception, setOpenedInterception] =
    useState<Interception | null>(null);
  const [openedInterceptionMember, setOpenedInterceptionMember] =
    useState<InterceptionMember | null>(null);
  const [openedInterceptionNetwork, setOpenedInterceptionNetwork] =
    useState<InterceptionNetwork | null>(null);

  const [selectedMembers, setSelectedMembers] = useState<InterceptionMember[]>(
    []
  );
  const [selectedNetworks, setSelectedNetworks] = useState<
    InterceptionNetwork[]
  >([]);

  const [search, setSearch] = useState('');
  const [searchUi, setSearchUi] = useState('');

  const setSearchValue = useDebouncedCallback((search: string) => {
    setSearch(search);
  }, 300);

  const dispatch = useAppDispatch();

  useEffect(() => {
    const memberIds = selectedMembers?.map((member) => member.id);
    const networkIds = selectedNetworks?.map((network) => network.id);
    dispatch(
      getInterceptionsListThunk({
        memberIds,
        networkIds,
        search,
      })
    );
  }, [selectedMembers, selectedNetworks, search]);

  useEffect(() => {
    dispatch(getInterceptionNetworksThunk({}));
    dispatch(getInterceptionMembersThunk({}));
    dispatch(getInterceptionDepartmentsThunk());
  }, []);

  const selectMember = (member: InterceptionMember) => {
    setSelectedMembers((members) =>
      !members.find((m) => m.id === member.id)
        ? [...members, member]
        : members.filter((m) => m.id !== member.id)
    );
  };

  const selectNetwork = (network: InterceptionNetwork) => {
    setSelectedNetworks((networks) =>
      !networks.find((n) => n.id === network.id)
        ? [...networks, network]
        : networks.filter((n) => n.id !== network.id)
    );
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const closeSidebar = () => {
    setOpenedInterception(null);
    setOpenedInterceptionNetwork(null);
    setOpenedInterceptionMember(null);
  };

  const openInterception = (interception: Interception) => {
    setOpenedInterception(interception);
    setOpenedInterceptionNetwork(null);
    setOpenedInterceptionMember(null);
  };

  const openInterceptionMember = (member: InterceptionMember) => {
    setOpenedInterception(null);
    setOpenedInterceptionNetwork(null);
    setOpenedInterceptionMember(member);
  };

  const openInterceptionNetwork = (network: InterceptionNetwork) => {
    setOpenedInterception(null);
    setOpenedInterceptionNetwork(network);
    setOpenedInterceptionMember(null);
  };

  return (
    <div>
      <Header className="w-full">
        <div className="flex justify-between items-center w-full ml-6">
          <div className="w-[411px]">
            <TextInput
              placeholder="Поиск по содержанию перехватов"
              theme="dark"
              searchIcon
              onChange={(value) => {
                setSearchUi(value);
                setSearchValue(value);
              }}
              value={searchUi}
            ></TextInput>
          </div>
          <div className="flex items-center gap-6 px-4 py-2">
            {selectedNetworks.length ? (
              <div className="text-[11px] tpg-c2">
                Выбрано сетей: {selectedNetworks.length}{' '}
                <span
                  className="text-bright_product ml-2 cursor-pointer"
                  onClick={() => setSelectedNetworks([])}
                >
                  Сбросить
                </span>
              </div>
            ) : null}
            {selectedMembers.length ? (
              <div className="text-[11px] tpg-c2">
                Выбрано абонентов: {selectedMembers.length}{' '}
                <span
                  className="text-bright_product ml-2 cursor-pointer"
                  onClick={() => setSelectedMembers([])}
                >
                  Сбросить
                </span>
              </div>
            ) : null}
          </div>
        </div>
      </Header>
      <div className="w-full mt-1">
        <InterceptionsFilterSidebar
          openInterceptionMember={openInterceptionMember}
          openInterceptionNetwork={openInterceptionNetwork}
          selectedNetworks={selectedNetworks}
          selectedMembers={selectedMembers}
          selectMember={selectMember}
          selectNetwork={selectNetwork}
          openedMember={openedInterceptionMember}
          openedNetwork={openedInterceptionNetwork}
        />
        {openedInterception ? (
          <InterceptionsInfoSidebar
            interception={openedInterception}
            closeSidebar={closeSidebar}
          />
        ) : null}
        {openedInterceptionNetwork ? (
          <InterceptionsNetworkInfoSidebar
            network={openedInterceptionNetwork}
            closeSidebar={closeSidebar}
          />
        ) : null}
        {openedInterceptionMember ? (
          <InterceptionsMemberInfoSidebar
            member={openedInterceptionMember}
            closeSidebar={closeSidebar}
          />
        ) : null}

        <div
          className={cn(
            `px-6 pb-[100px] ml-[450px] transition-[width,margin] duration-300 ease-in-out h-full overflow-auto absolute`,
            {
              'w-[calc(100%-970px)]':
                openedInterception ||
                openedInterceptionMember ||
                openedInterceptionNetwork,
              'w-[calc(100%-450px)]': !(
                openedInterception ||
                openedInterceptionMember ||
                openedInterceptionNetwork
              ),
            }
          )}
        >
          {/*<div className="rounded-[16px]  mx-auto  w-[200px] text-center bg-light px-4 py-1 font-bold">*/}
          {/*  3 новых сеанса*/}
          {/*</div>*/}
          <div className="mt-[24px]">
            <InterceptionsTable
              selectInterception={(interception) =>
                openInterception(interception)
              }
              openedInterceptionId={openedInterception?.id}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterceptionsScreen;
